import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Heading, Text, TextField, Tooltip } from "@telia/teddy";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../models";
import { getIsD2DOrTMDealer } from "../../../state/selectors/selectors";
import { getSimNumberError } from "../../../views/Dashboard/simCardUtils";
import type { SimDeliveryType } from "../../../views/Dashboard/simCardUtils";
import "./RetailerSubscriptionSelectable.less";

export enum SimChoice {
    Physical = "physical",
    ESim = "esim",
}

export interface SimConfig {
    simChoice: SimChoice;
    simNumber?: string;
}

interface Props {
    description?: string;
    disabledReason?: string;
    id: string;
    isDisabled?: boolean;
    name: string;
    handleAddServiceToCart: (simConfig?: SimConfig) => void;
    handleRemoveFromCart?: () => void;
    isLoading?: boolean;
    price: string | number;
    priceStriketrough?: string | number;
    priceSuffix?: string;
    simType?: string;
}

interface SimExpandSectionProps {
    onConfirm: (config: SimConfig) => void;
    isSubmitting?: boolean;
}

export const SimExpandSection: React.FC<SimExpandSectionProps> = ({ onConfirm, isSubmitting }) => {
    const isD2DOrTM = useSelector((state: RootState) => getIsD2DOrTMDealer(state));
    const defaultChoice = SimChoice.Physical;
    const [simChoice, setSimChoice] = useState<SimChoice | "">(defaultChoice);
    const [simNumber, setSimNumber] = useState("");
    const [simDelivery, setSimDelivery] = useState<SimDeliveryType>("from_store");
    const [simNumberError, setSimNumberError] = useState<string | null>(null);
    const simNumberInputRef = useRef<HTMLInputElement>(null);
    // D2D/TM dealers always send SIM cards home. Other retailers can choose, and only
    // register a SIM number when the SIM card is handed out in store.
    const canChooseSimDelivery = simChoice === SimChoice.Physical && !isD2DOrTM;
    const requiresSimNumber = canChooseSimDelivery && simDelivery === "from_store";

    useEffect(() => {
        if (requiresSimNumber) {
            setTimeout(() => {
                simNumberInputRef.current?.focus();
            }, 0);
        }
    }, [requiresSimNumber]);

    const handleConfirm = () => {
        if (!simChoice) return;

        if (requiresSimNumber) {
            const error = getSimNumberError(simNumber);
            if (error) {
                setSimNumberError(error);
                return;
            }
        }

        setSimNumberError(null);
        onConfirm({
            simChoice,
            simNumber: requiresSimNumber ? simNumber : undefined,
        });
    };

    return (
        <>
            <Text variant="paragraph-100" className="subscriptionSelectable__simLabel">
                Velg SIM-type:
            </Text>
            <Chip
                type="single"
                value={simChoice}
                onValueChange={(val) => {
                    if (val) {
                        setSimChoice(val as SimChoice);
                        setSimNumberError(null);
                    }
                }}
                variant="choice"
                className="subscriptionSelectable__simChips"
            >
                <Chip.Item value={SimChoice.Physical}>Fysisk SIM-kort</Chip.Item>
                <Chip.Item value={SimChoice.ESim}>eSIM</Chip.Item>
            </Chip>

            {canChooseSimDelivery && (
                <Box mt="200">
                    <Chip
                        type="single"
                        value={simDelivery}
                        onValueChange={(val) => {
                            if (val) {
                                setSimDelivery(val as SimDeliveryType);
                                setSimNumberError(null);
                            }
                        }}
                        variant="choice"
                        className="subscriptionSelectable__simChips"
                    >
                        <Chip.Item value="from_store">Fra butikk</Chip.Item>
                        <Chip.Item value="sent_home">Sendt hjem</Chip.Item>
                    </Chip>
                </Box>
            )}

            {requiresSimNumber && (
                <Box mt="200">
                    <div className="subscriptionSelectable__simNumberField">
                        <TextField>
                            <TextField.Label>SIM-nummer</TextField.Label>
                            <TextField.Input
                                ref={simNumberInputRef}
                                type="text"
                                value={simNumber}
                                onChange={(e) => {
                                    const digits = (e.target as HTMLInputElement).value.replace(/\D/g, "");
                                    setSimNumber(digits);
                                    setSimNumberError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleConfirm();
                                    }
                                }}
                            />
                        </TextField>
                        {simNumberError && (
                            <Text variant="paragraph-100" className="subscriptionSelectable__simNumberError">
                                {simNumberError}
                            </Text>
                        )}
                    </div>
                </Box>
            )}

            {simChoice && (
                <Box mt="200">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleConfirm}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                    >
                        Bekreft
                    </Button>
                </Box>
            )}
        </>
    );
};

const RetailerSubscriptionSelectable: React.FC<Props> = ({
    id,
    name,
    description,
    disabledReason,
    price,
    isLoading,
    priceStriketrough,
    handleAddServiceToCart,
    handleRemoveFromCart,
    priceSuffix = ",-/md.",
    isDisabled = false,
    simType,
}) => {
    const [isPendingSimAdd, setIsPendingSimAdd] = useState(false);

    const handleVelgClick = () => {
        if (simType) {
            setIsPendingSimAdd(true);
        } else {
            handleAddServiceToCart();
        }
    };

    const velgButton = isPendingSimAdd ? (
        <Button
            variant="secondary"
            className="subscriptionSelectable__selectButton"
            aria-label={`Avbryt ${name}`}
            onClick={() => setIsPendingSimAdd(false)}
        >
            Avbryt
        </Button>
    ) : (
        <Button
            variant="secondary"
            className="subscriptionSelectable__selectButton"
            aria-label={`Legg til ${name}`}
            loading={isLoading}
            disabled={isDisabled}
            onClick={handleVelgClick}
        >
            Legg til
        </Button>
    );

    const selectControl =
        !isPendingSimAdd && isDisabled && disabledReason ? (
            <Tooltip>
                <Tooltip.Trigger asChild>{velgButton}</Tooltip.Trigger>
                <Tooltip.Content className="subscriptionSelectable__tooltipContent">
                    <Box p="100" maxWidth="300px">
                        {disabledReason}
                    </Box>
                </Tooltip.Content>
            </Tooltip>
        ) : (
            velgButton
        );

    const showExpand = isPendingSimAdd && !!simType;

    return (
        <div id={id} className="subscriptionSelectable">
            <div className="subscriptionSelectable__container">
                <div className="subscriptionSelectable__inner">
                    <div className="subscriptionSelectable__leftWrapper">
                        {description && (
                            <Tooltip>
                                <Tooltip.Trigger className="subscriptionSelectable__tooltipTrigger" />
                                <Tooltip.Content className="subscriptionSelectable__tooltipContent">
                                    <Box p="100" maxWidth="300px">
                                        {description}
                                    </Box>
                                </Tooltip.Content>
                            </Tooltip>
                        )}

                        <Heading as="h4" variant="title-100" className="subscriptionSelectable__name">
                            {name}
                        </Heading>
                    </div>
                    <div className="subscriptionSelectable__rightWrapper">
                        {!!price && (
                            <div className="subscriptionSelectable__priceWrapper">
                                {price && (
                                    <Heading
                                        mb="100"
                                        as="h2"
                                        variant="subsection-100"
                                        title={`${price}`}
                                        className="subscriptionSelectable__price"
                                    >
                                        {price}
                                        {priceSuffix}
                                    </Heading>
                                )}
                                {priceStriketrough && (
                                    <div className="subscriptionSelectable__priceStriketroughWrapper">
                                        <span className="subscriptionSelectable__priceStriketroughDisclaimer">
                                            ord. pris:
                                        </span>
                                        <div className="subscriptionSelectable__priceStriketrough">
                                            {priceStriketrough}
                                            {priceSuffix}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {handleRemoveFromCart ? (
                            <Button
                                variant="tertiary-purple"
                                className="subscriptionSelectable__selectButton"
                                aria-label={`Fjern ${name}`}
                                loading={isLoading}
                                onClick={handleRemoveFromCart}
                            >
                                Fjern
                            </Button>
                        ) : (
                            selectControl
                        )}
                    </div>
                </div>
            </div>

            {showExpand && (
                <div
                    className="subscriptionSelectable__simExpand"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <SimExpandSection
                        onConfirm={(config) => {
                            handleAddServiceToCart(config);
                            setIsPendingSimAdd(false);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default RetailerSubscriptionSelectable;
